import React, { Suspense, useRef, useEffect, useState } from 'react'
import { Layout, Button, Dropdown, message } from 'antd'
import loadable from '@loadable/component'
import { getTempEnvParam } from 'env'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { isGlobal } from 'conf'
import { featureFlags } from 'config/feature-flags'
import { getSearchParams, setSearchParams } from 'utils'
import { openloadingAni, errorBase } from 'utils/ui'

import TopBar from 'components/cleanui/layout/TopBar'
import Menu from 'components/cleanui/layout/Menu'
import RecordMenu from 'pages/records/components/RecordMenu'
import UserService from 'api/Users'
import { QUOTA_MODE } from 'types/utils'
import plusIcon from '@/assets/images/icon-create-plus.svg'

import styles from './style.module.scss'
// import WorkspaceSelect from './components/WorkspaceSelect'

// BlockNote 에디터 번들이 무거워 메모 팝업을 열 때만 로드한다
const CreateMemoModal = loadable(() => import('./components/CreateMemoModal'))

// SearchTablePage 의 mode=create 처럼, 메모 모달도 URL mode 파라미터로 상태를 기억한다
const MEMO_CREATE_MODE = 'memo-create'

const mapStateToProps = ({ settings, menu, user }) => ({
  disableCreate: menu.disableCreate,
  isContentMaxWidth: settings.isContentMaxWidth,
  isAppMaxWidth: settings.isAppMaxWidth,
  isGrayBackground: settings.isGrayBackground,
  isSquaredBorders: settings.isSquaredBorders,
  isCardShadow: settings.isCardShadow,
  isBorderless: settings.isBorderless,
  isTopbarFixed: settings.isTopbarFixed,
  isGrayTopbar: settings.isGrayTopbar,
  // workspaceId: user.workspace.id,
  license: user.paidLicense,
})

const MainLayout = ({
  children,
  disableCreate,
  isContentMaxWidth,
  isAppMaxWidth,
  isGrayBackground,
  isSquaredBorders,
  isCardShadow,
  isBorderless,
  isTopbarFixed,
  isGrayTopbar,
  license,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // 메모 모달은 닫힐 때 unmount 되므로, 저장 성공 토스트가 사라지지 않도록
  // messageApi 를 레이아웃에서 만들어 prop 으로 내린다 (RecordEdit 의 messageApi prop 패턴)
  const [messageApi, messageContextHolder] = message.useMessage()

  const openItem = () => {
    const tablePage = getTempEnvParam('curTablePage')

    dispatch({
      type: 'settings/SET_STATE',
      payload: {
        isMobileMenuOpen: false,
      },
    })

    if (tablePage != null) {
      // tablePage.openItem(null)

      // setTimeout(() => {
      tablePage.openItem('create', null)
      // }, 100)
    }
  }

  const handleCreatePassword = async () => {
    if (isClickingRef.current) return
    isClickingRef.current = true

    try {
      const tablePage = getTempEnvParam('curTablePage')

      // 레코드 목록(SearchTablePage)이 마운트되지 않은 화면에선 열 대상이 없다 —
      // 가드 없이 openItemType 접근 시 TypeError 크래시 (openItem() 의 null 가드와 동일 사유)
      if (tablePage == null) return

      if (tablePage.openItemType !== 'create') {
        if (isGlobal() && license.quota_mode === QUOTA_MODE.RESOURCE) {
          try {
            const res = await UserService.getMyResourceQuota()

            const {
              user_quota: userQuota,
              workspace_quota: workspaceQuota,
              owner_info: ownerInfo,
            } = res.data

            const isUserQuotaExceeded = userQuota.current_resources >= userQuota.max_resources
            const isWorkspaceQuotaExceeded =
              workspaceQuota.current_resources >= workspaceQuota.max_resources

            if (isUserQuotaExceeded || isWorkspaceQuotaExceeded) {
              const workspaceOwnerEmail = ownerInfo.email
              const quotaType = isUserQuotaExceeded
                ? t('common.quota.user')
                : t('common.quota.workspace')

              errorBase({
                icon: 'red',
                title: t('record.title.maxLimitReached'),
                content: t('record.desc.maxLimitReached', { quotaType, workspaceOwnerEmail }),
                width: 400,
                okText: t('common.button.ok'),
                onOk: () => {},
              })
              return
            }
          } catch (error) {
            console.error('Error fetching resource quota:', error)
          }
        }

        openItem()
      }
    } finally {
      isClickingRef.current = false
    }
  }

  // mode=memo-create 파라미터가 있으면 새로고침 후에도 메모 모달을 복원.
  // 단 memoTemplate flag OFF 빌드에선 URL 에 mode 가 남아 있어도 복원하지 않는다.
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(
    () => featureFlags.memoTemplate && getSearchParams().mode === MEMO_CREATE_MODE,
  )

  // 메모 에디터 청크(BlockNote+Mantine, 대형)를 유휴 시간에 미리 받아둔다 —
  // lazy 청크를 클릭 시점에 받으면 팝업이 늦게 뜨므로 첫 오픈 체감 지연 제거.
  // flag OFF 빌드에선 메모 진입 경로가 없으므로 무거운 청크를 미리 받지 않는다.
  // (테스트의 jest.mock 컴포넌트엔 preload 가 없어 옵셔널 호출)
  useEffect(() => {
    if (!featureFlags.memoTemplate) return undefined
    const preload = () => CreateMemoModal.preload?.()
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(preload, { timeout: 5000 })
      return () => window.cancelIdleCallback(idleId)
    }
    const timerId = setTimeout(preload, 3000)
    return () => clearTimeout(timerId)
  }, [])

  const handleCreateMemo = () => {
    // 이미 메모 생성 팝업이 떠 있으면 무시 — URL 재기록으로 상태가 흔들리지 않게 명시 가드
    if (isMemoModalOpen) return
    setSearchParams({ mode: MEMO_CREATE_MODE })
    setIsMemoModalOpen(true)
  }

  const handleCloseMemo = () => {
    setIsMemoModalOpen(false)
    // 다른 화면이 쓰는 mode 값(create 등)을 지우지 않도록 memo-create 일 때만 제거
    if (getSearchParams().mode === MEMO_CREATE_MODE) {
      setSearchParams({ mode: undefined })
    }
  }

  // 생성 드롭다운 항목(패스워드/메모). 드롭다운은 memoTemplate flag ON 일 때만 렌더되므로
  // (아래 preElem 참고) 메모 항목은 항상 포함한다.
  const createMenuItems = [
    { key: 'password', label: t('appConfig.menu.createPassword') },
    { key: 'memo', label: t('appConfig.menu.createMemo') },
  ]

  const handleCreateMenuClick = ({ key }) => {
    if (key === 'password') {
      handleCreatePassword()
    } else if (key === 'memo') {
      handleCreateMemo()
    }
  }

  const loading = useSelector((state) => state.user.loading)
  const loadingRef = useRef(null)
  const isClickingRef = useRef(false)

  useEffect(() => {
    if (loading) {
      loadingRef.current = openloadingAni({
        text: t('logOut.desc.progress' || 'Loading...'),
      })
    } else if (loadingRef.current) {
      loadingRef.current.destroy()
      loadingRef.current = null
    }
  }, [loading])

  const preElem = (
    <div
      className={classNames(styles.btnWrapper)}
      style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
    >
      {/* {isGlobal() && <WorkspaceSelect />} */}
      {/* memoTemplate flag 로 게이팅: ON 이면 패스워드/메모 드롭다운, OFF 면 드롭다운 없이
          '패스워드 생성' 단일 버튼(생성 패널 직접 오픈)으로 기존 동작 유지 */}
      {featureFlags.memoTemplate ? (
        <Dropdown
          menu={{ items: createMenuItems, onClick: handleCreateMenuClick }}
          trigger={['click']}
          disabled={disableCreate}
          overlayClassName="menuStyle2"
          // 드롭다운을 여는 순간에도 preload — 유휴 preload 가 아직 안 돈 경우의 보험
          onOpenChange={(open) => open && CreateMemoModal.preload?.()}
        >
          <Button className={classNames(styles.createBtn, 'type2', 'h48')} disabled={disableCreate}>
            <img src={plusIcon} alt="plus" />
            {t('appConfig.title.createNew')}
          </Button>
        </Dropdown>
      ) : (
        <Button
          className={classNames(styles.createBtn, 'type2', 'h48')}
          disabled={disableCreate}
          onClick={handleCreatePassword}
        >
          <img src={plusIcon} alt="plus" />
          {t('appConfig.title.createPassword')}
        </Button>
      )}
    </div>
  )

  return (
    <div className={classNames('officemail', { cui__layout__grayBackground: isGrayBackground })}>
      <Layout
        className={classNames({
          cui__layout__contentMaxWidth: isContentMaxWidth,
          cui__layout__appMaxWidth: isAppMaxWidth,
          cui__layout__grayBackground: isGrayBackground,
          cui__layout__squaredBorders: isSquaredBorders,
          cui__layout__cardsShadow: isCardShadow,
          cui__layout__borderless: isBorderless,
        })}
      >
        <Layout.Header
          className={classNames('cui__layout__header', styles.header, {
            cui__layout__fixedHeader: isTopbarFixed,
            cui__layout__headerGray: isGrayTopbar,
          })}
        >
          <TopBar />
        </Layout.Header>
        <React.Suspense fallback={<div>{t('common.title.loading')}</div>} />
        <Layout.Content className={classNames(styles.mainFrame)}>
          <Suspense fallback={<div>Loading...</div>}>
            <Layout>
              <Menu preElem={preElem} menuType="user" leftMenuComp={RecordMenu} />
              <Layout>
                <Layout.Content style={{ height: '100%', position: 'relative' }}>
                  <div className={classNames(styles.content)}>{children}</div>
                </Layout.Content>
              </Layout>
            </Layout>
          </Suspense>
        </Layout.Content>
      </Layout>
      {messageContextHolder}
      {/* 열 때마다 mount 해서 에디터 내용이 초기화되도록 조건부 렌더링 */}
      {isMemoModalOpen && <CreateMemoModal onClose={handleCloseMemo} messageApi={messageApi} />}
    </div>
  )
}

export default connect(mapStateToProps)(MainLayout)
